import React, { Component } from 'react';
import styled from '@emotion/styled';

const appearanceAnimationTime = 225;

const Wrapper = styled.div<{
  isVisible: boolean;
  show: boolean;
}>`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${({ isVisible }) => (isVisible ? 1300 : -1)};
  position: fixed;
  opacity: ${({ show }) => (show ? 1 : 0)};
  transition: opacity ${appearanceAnimationTime}ms;
`;

const Background = styled.div`
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: -1;
  position: fixed;
  background-color: rgba(0, 0, 0, 0.5);
`;

const Backdrop = styled.div`
  height: 100%;
  outline: none;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: inherit;
`;

const Modal = styled.div`
  max-width: 500px;
  flex: 0 1 auto;
  max-height: calc(100% - 96px);
  margin: 48px;
  display: flex;
  position: relative;
  overflow-y: auto;
  flex-direction: column;
  border-radius: 4px;
  background-color: #fff;
  box-sizing: inherit;
  padding: 20px;
`;

const Footer = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const Title = styled.div`
  flex: 0 0 auto;
  margin-bottom: 20px;
  color: rgba(0, 0, 0, 0.87);
  font-size: 1.5rem;
  font-weight: 500;
  color: #333;
  letter-spacing: 0.0075em;
`;

const Content = styled.div`
  flex: 1 1 auto;
  color: #666;
  margin-bottom: 30px;
  overflow-y: auto;
  line-height: 1.5;
  font-size: 1rem;
  font-weight: 400;
  line-height: 1.75;
`;

const Button = styled.button<{
  primary?: boolean;
}>`
  padding: 6px 8px;
  font-size: 0.875rem;
  min-width: 64px;
  box-sizing: border-box;
  line-height: 1.75;
  font-weight: 500;
  border-radius: 2px;
  letter-spacing: 0.02857em;
  text-transform: uppercase;
  transition: background-color 250ms;
  margin-left: 15px;
  cursor: pointer;
  box-shadow: 0px 1px 2px rgba(0, 0, 0, 0.2);

  background: ${(props) => (props.primary ? '#465fe6' : '#fff')};
  color: ${(props) => (props.primary ? '#fff' : '#333')};
`;

interface Props {
  show: boolean;
  title?: string | React.ReactElement;
  content?: string | React.ReactElement;
  disagreeButton?: string | React.ReactElement;
  agreeButton?: string | React.ReactElement;
  onClose: () => void;
  onConfirm?: () => void;
}

interface State {
  isVisible: boolean;
}

class ModalComponent extends Component<Props, State> {
  state = {
    isVisible: false,
  };

  componentDidUpdate(props: Props) {
    this.animationPropsToState(props);
  }

  animationPropsToState = (props: Props) => {
    const { show } = this.props;

    if (show && !props.show) {
      this.setState({ isVisible: true });
    } else if (!show && props.show) {
      setTimeout(() => this.setState({ isVisible: false }), appearanceAnimationTime);
    }
  };

  handleAction = (action: () => void) => {
    setTimeout(() => action(), appearanceAnimationTime);
  };

  render() {
    const {
      children,
      show,
      title,
      content,
      disagreeButton,
      onClose,
      agreeButton,
      onConfirm,
    } = this.props;
    const { isVisible } = this.state;

    return (
      <Wrapper show={show} isVisible={isVisible}>
        <Background />
        <Backdrop onTouchStart={() => this.handleAction(onClose)}>
          <Modal onTouchStart={(e) => e.stopPropagation()}>
            {title && <Title>{title}</Title>}
            {content && <Content>{content}</Content>}
            {children}
            <Footer>
              {disagreeButton && (
                <Button onTouchStart={() => this.handleAction(onClose)}>{disagreeButton}</Button>
              )}
              {agreeButton && onConfirm && (
                <Button primary onTouchStart={() => this.handleAction(onConfirm)}>
                  {agreeButton}
                </Button>
              )}
            </Footer>
          </Modal>
        </Backdrop>
      </Wrapper>
    );
  }
}

export default ModalComponent;
